<script setup lang="ts">
import { ref, watch, onMounted, toRefs } from 'vue';
import Dialog from 'primevue/dialog';
import { EMIT } from '@/enum';
import { useClinicianOrganizationStore } from '@/store';

type Clinician = {
  id: number;
  display_name: string;
};

type ErrorsType = {
  name?: string;
};

type ModeType = 'add' | 'edit';

const props = defineProps<{
  organizationId: number;
  mode?: ModeType;
  initialName?: string;
  initialSelectedClinicians?: Clinician[];
}>();

const { organizationId, mode, initialName, initialSelectedClinicians } = toRefs(props);

const organizationStore = useClinicianOrganizationStore();
const emit = defineEmits([EMIT.CANCEL, EMIT.SAVE, EMIT.CLOSE, EMIT.EDIT]);
const visible = ref(true);
const errors = ref<ErrorsType>({});
const name = ref(initialName.value ?? '');
const selectedClinicians = ref<Clinician[]>(initialSelectedClinicians.value ?? []);
const filters = ref({
  global: { value: null, matchMode: 'contains' },
});

watch(visible, (val: boolean) => {
  if (!val) emit(EMIT.CANCEL);
});

const getIdArray = (arr: Clinician[] | number[]): number[] => arr.map((c) => (typeof c === 'object' ? c.id : c));

const validateForm = (): boolean => {
  errors.value = {};
  if (!name.value) errors.value.name = 'Building name is required.';
  return !errors.value.name;
};

const submit = async (): Promise<void> => {
  if (!validateForm()) return;

  if (mode.value === 'add') {
    emit(EMIT.SAVE, {
      name: name.value,
      attachClinicians: selectedClinicians.value.map((c) => c.id),
    });
  } else if (mode.value === 'edit') {
    const initialIds = getIdArray(initialSelectedClinicians.value ?? []);
    const currentIds = getIdArray(selectedClinicians.value);

    const attachClinicians = currentIds.filter((id) => !initialIds.includes(id));
    const detachClinicians = initialIds.filter((id) => !currentIds.includes(id));

    emit(EMIT.EDIT, {
      name: name.value,
      attachClinicians,
      detachClinicians,
    });
  }
};

onMounted(async () => {
  await organizationStore.getCliniciansForOrganizationRequest(organizationId.value);
});
</script>

<template>
  <Dialog
    v-model:visible="visible"
    modal
    :header="mode === 'edit' ? 'Edit Building' : 'Add Building'"
    :closable="true"
    :draggable="false"
    class="dialog__container"
  >
    <div class="field">
      <label class="required">Building Name</label>
      <InputText v-model="name" class="w-full" />
      <small v-if="errors.name" class="p-error">{{ errors.name }}</small>
    </div>
    <DataTable
      v-model:selection="selectedClinicians"
      :value="organizationStore.clinicians"
      :loading="organizationStore.loading"
      data-key="id"
      responsive-layout="scroll"
      :sort-field="'display_name'"
      :sort-order="1"
      :filters="filters"
      :global-filter-fields="['display_name']"
    >
      <template #header>
        <label class="pt-1 mb-2 ml-3 block">
          {{ mode === 'edit' ? 'Edit Service Providers' : 'Add Service Providers' }}
        </label>
        <div class="search-input pb-1 mb-3 ml-3 flex">
          <IconField icon-position="left">
            <InputIcon>
              <Icon name="search" class="text-lg" />
            </InputIcon>
            <InputText v-model="filters.global.value" placeholder="Search service provider" />
          </IconField>
        </div>
      </template>

      <Column selection-mode="multiple" header-style="width:3rem"></Column>

      <Column field="display_name" header="Name" sortable>
        <template #body="{ data }">
          {{ data.display_name }}
        </template>
      </Column>
    </DataTable>
    <template #footer>
      <AppButton text="Cancel" type="secondary" @click="$emit(EMIT.CANCEL)" />
      <AppButton text="Save" type="primary" @click="submit" />
    </template>
  </Dialog>
</template>
